import { TypeChecker } from '@attain-apps/type-guards'

/**
 * A BCH expense category label used to group balances and justification text.
 *
 * Labels stay plain strings rather than a union, because they are read from the
 * 53A report's header row: the reports are human-maintained, and a column we do
 * not recognize still has to survive parsing.
 */
export type ExpenseCategory = string
export namespace ExpenseCategory {
  export const isExpenseCategory: TypeChecker<ExpenseCategory> =
    TypeChecker.isString

  /**
   * The label for calculated overhead, which is the twentieth column of the
   * justification tab and always its last figure.
   */
  export const INDIRECT_COSTS = 'Indirect Costs'

  /**
   * The nineteen expense detail categories, in worksheet column order.
   *
   * Order is load-bearing: the client's justification formula joins the columns
   * left to right (`TEXTJOIN` over `B:U`), so this sequence is the order the
   * category list reads in the sentence sent to the sponsor.
   *
   * `source` is the header on the 53A and `DOC#` tabs; `display` is the header
   * on the `New Justifications` tab, which is what the sponsor sees. Where the
   * two differ, the client renamed the category for the sponsor.
   *
   * `overheadEligible` reproduces the client's overhead formula, which sums the
   * included columns rather than subtracting the excluded ones:
   *
   * ```
   * Overhead = ROUND((C+D+E+F+H+I+J+K+L+P+S) * OH Rate, 2)
   * ```
   *
   * `Other` is eligible, which is what makes subawards under $25,000 attract
   * overhead — the outline notes they are coded there.
   */
  export const DETAILS = [
    { display: 'Personnel', overheadEligible: true, source: 'Salary and Wage' },
    { display: 'Stipends', overheadEligible: true, source: 'Stipends' },
    {
      display: 'Fringe Benefits',
      overheadEligible: true,
      source: 'Fringe Benefits',
    },
    { display: 'Consulting', overheadEligible: true, source: 'Consultant' },
    {
      display: 'Capital Equipment',
      overheadEligible: false,
      source: 'Capital Equipment',
    },
    {
      display: 'Minor Equipment',
      overheadEligible: true,
      source: 'Minor Equipment',
    },
    { display: 'Supplies', overheadEligible: true, source: 'Supplies' },
    {
      display: 'Animal Boarding',
      overheadEligible: true,
      source: 'Animal Boarding',
    },
    {
      display: 'Foreign Travel',
      overheadEligible: true,
      source: 'Foreign Travel',
    },
    {
      display: 'Domestic Travel',
      overheadEligible: true,
      source: 'Domestic Travel',
    },
    {
      display: 'Inpatient Care',
      overheadEligible: false,
      source: 'Inpatient Care',
    },
    {
      display: 'Outpatient Care',
      overheadEligible: false,
      source: 'Outpatient Care',
    },
    {
      display: 'Renovations',
      overheadEligible: false,
      source: 'Alterations & Renovations',
    },
    { display: 'Other', overheadEligible: true, source: 'Other' },
    {
      display: 'Contractual/Subawards',
      overheadEligible: false,
      source: 'Contracts & Third Party',
    },
    {
      display: 'Trainee Tuition',
      overheadEligible: false,
      source: 'Trainee Tuition',
    },
    {
      display: 'Trainee Travel',
      overheadEligible: true,
      source: 'Trainee Travel',
    },
    { display: 'Transfers', overheadEligible: false, source: 'Transfers' },
    { display: 'No Mapping', overheadEligible: false, source: 'No Mapping' },
  ] as const satisfies readonly Detail[]

  /** One expense detail category and how it is treated. */
  export interface Detail {
    /** Header on the 53A and `DOC#` tabs. */
    readonly source: ExpenseCategory
    /** Header on the `New Justifications` tab; what the sponsor reads. */
    readonly display: string
    /** Whether the category is part of the modified total direct cost base. */
    readonly overheadEligible: boolean
  }

  /**
   * The twenty justification columns in worksheet order: the detail categories
   * followed by calculated overhead.
   */
  export const DISPLAY_ORDER: readonly string[] = [
    ...DETAILS.map(detail => detail.display),
    INDIRECT_COSTS,
  ]

  const DETAIL_BY_SOURCE = new Map(
    DETAILS.map(detail => [canonicalize(detail.source), detail]),
  )

  const DISPLAY_INDEX = new Map(
    DISPLAY_ORDER.map((display, index) => [canonicalize(display), index]),
  )

  /**
   * The sponsor-facing label for a source category.
   *
   * A category we do not recognize keeps its own label, so an added 53A column
   * still reaches the reviewer instead of disappearing.
   */
  export function toDisplay(category: ExpenseCategory): string {
    return DETAIL_BY_SOURCE.get(canonicalize(category))?.display ?? category
  }

  /**
   * Whether a source category belongs to the overhead base.
   *
   * Unrecognized categories are excluded, matching the client's formula: it
   * names the columns that attract overhead, so anything unnamed does not. That
   * under-claims overhead on an unknown column rather than over-claiming it,
   * which is the safe direction for a federal draw.
   */
  export function isOverheadEligible(category: ExpenseCategory): boolean {
    return (
      DETAIL_BY_SOURCE.get(canonicalize(category))?.overheadEligible ?? false
    )
  }

  /**
   * Sort key placing a sponsor-facing label in worksheet column order.
   *
   * Labels outside the twenty sort after them, keeping the familiar sentence
   * intact and making an unexpected category visible at its end.
   */
  export function displayOrderIndex(display: string): number {
    return DISPLAY_INDEX.get(canonicalize(display)) ?? DISPLAY_ORDER.length
  }

  /**
   * Reduces a label to a stable comparison key.
   *
   * BCH's workbooks are hand-maintained, so case, spacing, and punctuation all
   * drift between months. `&` becomes `and` before punctuation is stripped, so
   * that `Alterations & Renovations` and `Alterations and Renovations` reach the
   * same category rather than differing by three letters.
   */
  export function canonicalize(value: unknown): string {
    return String(value ?? '')
      .toLowerCase()
      .replace(/&/g, ' and ')
      .replace(/[^a-z0-9]/g, '')
  }
}
